import { useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useFreshness } from "../api/hooks";
import type { DurationStat, ResponseTimeRow, Severity } from "../api/types";
import { can } from "../auth/roles";
import { GROUP_BY, HYDRANT_CONDITION, SEVERITY, SEVERITY_ORDER, labelOf, statusOf } from "../components/labels";
import { QueryState, StaleNotice } from "../components/ProblemAlert";
import { Tabs } from "../components/Tabs";
import { useFireContext } from "../context";
import { addDays, formatDate, formatDateTime, formatSeconds, lusakaToday } from "../format/datetime";
import { showMoney } from "../format/money";
import { ScreenHeader, useConfigNames, useStationNames, useWardNames } from "./shared";

/** The contract's longest report window: `to` at most 366 days after `from`. */
export const MAX_REPORT_DAYS = 366;

/** Why a report range cannot be asked for, in words, or null. */
export function rangeError(from: string, to: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return "Choose both dates.";
  const days = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
  if (days < 0) return "The end date is before the start date.";
  if (days > MAX_REPORT_DAYS) return `A report covers at most ${MAX_REPORT_DAYS} days after its start. Narrow the dates.`;
  return null;
}

const stat = (s: DurationStat) => (s.n === 0 ? "—" : `${formatSeconds(s.median_s)} / ${formatSeconds(s.p90_s)}`);
/** A share 0..1 as a whole percentage (display only; not money). */
const share = (x: number | null | undefined) => (x == null ? "—" : `${Math.round(x * 100)}%`);

function ResponseTimes() {
  const { now } = useFireContext();
  const names = useConfigNames();
  const wardName = useWardNames();
  const stationName = useStationNames();
  const today = lusakaToday(now());
  const [from, setFrom] = useState(addDays(today, -29));
  const [to, setTo] = useState(today);
  const [groupBy, setGroupBy] = useState("none");
  const [severity, setSeverity] = useState<Severity | "">("");
  const err = rangeError(from, to);
  const q = useApi(["response-times", from, to, groupBy, severity], (api) => endpoints.getResponseTimes(api, { from, to, groupBy, severity: severity ? [severity] : undefined }), {
    enabled: !err,
    keepPrevious: true,
  });
  const label = (r: ResponseTimeRow) => {
    if (r.key === null) return groupBy === "none" ? "All incidents" : "Not known";
    if (r.label) return r.label;
    if (groupBy === "ward") return wardName(r.key);
    if (groupBy === "incident_type") return names.incidentType(r.key);
    if (groupBy === "station") return stationName(r.key);
    return r.key;
  };
  const rows = q.data ? (groupBy === "none" ? [q.data.total] : q.data.rows) : [];
  return (
    <>
      <div className="fer-toolbar">
        <label className="fer-field fer-field--inline">
          <span>From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="fer-field fer-field--inline">
          <span>To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="fer-field fer-field--inline">
          <span>Group by</span>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            {Object.entries(GROUP_BY).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="fer-field fer-field--inline">
          <span>Severity</span>
          <select value={severity} onChange={(e) => setSeverity(e.target.value as Severity | "")}>
            <option value="">All</option>
            {SEVERITY_ORDER.map((s) => (
              <option key={s} value={s}>
                {statusOf(SEVERITY, s).label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {err && (
        <p className="fer-form-error" role="alert">
          {err}
        </p>
      )}
      <p className="fer-hint">
        Incidents created from {formatDate(from)} to {formatDate(to)} (Lusaka days), not cancelled. Times are median / 90th percentile; call to
        dispatch runs from the first alert. Incidents cancelled after a dispatch are counted apart.
      </p>
      <QueryState isLoading={q.isLoading} error={q.error} label="the report" />
      {rows.length > 0 && (
        <table className="lcc-table" aria-label="Response times">
          <thead>
            <tr>
              <th scope="col">{labelOf(GROUP_BY, groupBy)}</th>
              <th scope="col" className="fer-num">
                Incidents
              </th>
              <th scope="col">Alert to incident</th>
              <th scope="col">Call to dispatch</th>
              <th scope="col">Dispatch to scene</th>
              <th scope="col" className="fer-num">
                Exact locations
              </th>
              <th scope="col" className="fer-num">
                Cancelled after dispatch
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key ?? `none-${i}`}>
                <th scope="row">{label(r)}</th>
                <td className="fer-num">{r.incidents}</td>
                <td>{stat(r.alert_to_incident)}</td>
                <td>
                  {stat(r.call_to_dispatch)} <span className="fer-hint">(n={r.call_to_dispatch.n})</span>
                </td>
                <td>
                  {stat(r.dispatch_to_scene)} <span className="fer-hint">(n={r.dispatch_to_scene.n})</span>
                </td>
                <td className="fer-num">{share(r.high_accuracy_share)}</td>
                <td className="fer-num">
                  {r.cancelled_after_dispatch}
                  {r.cancelled_after_dispatch > 0 ? ` (call to dispatch ${stat(r.cancelled_call_to_dispatch)})` : ""}
                </td>
              </tr>
            ))}
            {groupBy !== "none" && q.data && (
              <tr className="fer-row--total">
                <th scope="row">All</th>
                <td className="fer-num">{q.data.total.incidents}</td>
                <td>{stat(q.data.total.alert_to_incident)}</td>
                <td>{stat(q.data.total.call_to_dispatch)}</td>
                <td>{stat(q.data.total.dispatch_to_scene)}</td>
                <td className="fer-num">{share(q.data.total.high_accuracy_share)}</td>
                <td className="fer-num">{q.data.total.cancelled_after_dispatch}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

function Snapshot() {
  const q = useApi(["snapshot"], endpoints.getSnapshot);
  const s = q.data;
  const fresh = useFreshness(q);
  if (!s) return <QueryState isLoading={q.isLoading} error={q.error} label="today's figures" />;
  const c = s.city;
  const figures: Array<[string, string]> = [
    ["Incidents today", String(c.incidents_today)],
    ["Open incidents", String(c.incidents_open)],
    ["Incidents in 30 days", String(c.incidents_30d)],
    ["Call to dispatch (30 days, median / 90th)", stat(c.call_to_dispatch_30d)],
    ["Dispatch to scene (30 days, median / 90th)", stat(c.dispatch_to_scene_30d)],
    ["Exact locations (30 days)", share(c.high_accuracy_share_30d)],
    ["Alerts in 30 days", String(c.alerts_30d)],
    ["SOS refused outside the area (30 days)", String(c.refused_sos_30d)],
    ["Appliances available / dispatched / out of service / off duty", `${c.appliances_available} / ${c.appliances_dispatched} / ${c.appliances_out_of_service} / ${c.appliances_off_duty}`],
    ["Hydrants", `${c.hydrants_total} (${Object.entries(c.hydrants_by_condition).map(([k, n]) => `${statusOf(HYDRANT_CONDITION, k).label.toLowerCase()} ${n}`).join(", ") || "none"})`],
    ["Inspections in 30 days (failed)", `${c.inspections_30d} (${c.inspections_failed_30d})`],
    ["Certificates valid (expiring in 30 days)", `${c.certificates_valid} (${c.certificates_expiring_30d})`],
    ["Certificate fees billed (30 days)", showMoney(c.certificate_fees_billed_30d)],
    ["Certificate fees collected (30 days)", showMoney(c.certificate_fees_collected_30d)],
  ];
  return (
    <>
      <StaleNotice {...fresh} error={q.error} what="figures" />
      <p className="fer-hint">
        Figures of {formatDate(s.service_date)} (Lusaka day) as of {formatDateTime(s.as_of)}. No personal data.
      </p>
      <dl className="fer-stats" aria-label="Today's figures">
        {figures.map(([t, v]) => (
          <div className="fer-stat" key={t}>
            <dt>{t}</dt>
            <dd>
              <span className="fer-stat__value">{v}</span>
            </dd>
          </div>
        ))}
      </dl>
    </>
  );
}

type Tab = "times" | "snapshot";

/** Response-time KPIs (median and 90th percentile) and today's figures for the chief, the FER admin and the city manager. */
export function ReportsScreen() {
  const { principal } = useFireContext();
  const tabs = [
    ...(can(principal, "viewResponseTimes") ? [{ key: "times" as const, label: "Response times" }] : []),
    ...(can(principal, "viewSnapshot") ? [{ key: "snapshot" as const, label: "Today's figures" }] : []),
  ];
  const [tab, setTab] = useState<Tab>(tabs[0]?.key ?? "times");
  const current = tabs.some((t) => t.key === tab) ? tab : (tabs[0]?.key ?? "times");
  return (
    <div className="fer-screen">
      <ScreenHeader title="Response times" />
      <Tabs label="Reports" tabs={tabs} current={current} onChange={setTab}>
        {current === "times" ? <ResponseTimes /> : <Snapshot />}
      </Tabs>
    </div>
  );
}
